import {API} from "../API/api";
import {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {Colors} from "../Styles/Colors";
import {GiMining } from "react-icons/gi";
import {IoPlanetOutline} from "react-icons/io5";
import {MdLocationOn, MdOutlineStorage} from "react-icons/md";
import {SiArtstation} from "react-icons/si";
import useIndustrialTasks from "../Provider/useIndustrialTasks";

type IndustrialLocation = {
	id: number;
	id_report: number;
	date_added: number;
	date_modified: number;
	id_star_system: number;
	id_planet: number;
	id_orbit: number;
	id_space_station: number;
	id_terminal: number;
	id_city: number;
	id_moon: number;
	id_outpost: number;
	id_poi: number;
	star_system_name: string | null;
	planet_name: string | null;
	orbit_name: string | null;
	space_station_name: string | null;
	terminal_name: string | null;
	city_name: string | null;
	moon_name: string | null;
	outpost_name: string | null;
	poi_name: string | null;
	value: number;
	value_week: number;
	value_month: number;
};

const Styles = {
	Page: styled.div`
		padding: 28px;
		color: ${Colors.raw.terminal_light};
		text-align: left;
	`,

	Header: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		gap: 18px;
		margin-bottom: 24px;

		@media (max-width: 840px) {
			align-items: flex-start;
			flex-direction: column;
		}
	`,

	TitleBlock: styled.div`
		display: grid;
		gap: 8px;
	`,

	Eyebrow: styled.div`
		color: ${Colors.raw.neon_orange};
		letter-spacing: 3px;
		text-transform: uppercase;
		font-size: 12px;
	`,

	Title: styled.h1`
		margin: 0;
		font-size: clamp(28px, 5vw, 48px);
		line-height: 1;
		text-transform: uppercase;
		text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subline: styled.div`
		max-width: 720px;
		opacity: 0.72;
		font-size: 13px;
		line-height: 1.45;
	`,

	Summary: styled.div`
		display: grid;
		grid-template-columns: repeat(3, minmax(110px, 1fr));
		gap: 10px;

		@media (max-width: 560px) {
			width: 100%;
			grid-template-columns: 1fr;
		}
	`,

	SummaryBox: styled.div`
		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.18), transparent 42%),
			rgba(0, 0, 0, 0.25);
		padding: 12px 14px;
		min-width: 120px;
		box-shadow: 0 0 16px rgba(255, 122, 0, 0.10);
	`,

	SummaryLabel: styled.div`
		opacity: 0.58;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.5px;
		margin-bottom: 6px;
	`,

	SummaryValue: styled.div`
		color: ${Colors.raw.neon_orange};
		font-family: "Courier New", monospace;
		font-size: 18px;
		font-weight: 800;
	`,

	Controls: styled.div`
		display: grid;
		grid-template-columns: 1fr 220px 220px;
		gap: 12px;
		align-items: center;
		margin-bottom: 22px;

		@media (max-width: 980px) {
			grid-template-columns: 1fr;
		}
	`,

	Input: styled.input`
		width: 100%;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.35);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		font-family: "Courier New", monospace;

		&::placeholder {
			color: rgba(255, 255, 255, 0.45);
		}

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	Select: styled.select`
		width: 100%;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.82);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		font-family: "Courier New", monospace;

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	Grid: styled.div`
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
		gap: 18px;
	`,

	Card: styled.article`
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 18px;

		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.20), transparent 36%),
			linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018));
		box-shadow: 0 0 20px rgba(255, 122, 0, 0.10);
		clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${Colors.raw.neon_orange};
		}
	`,

	CardTop: styled.div`
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 12px;
		align-items: flex-start;
	`,

	IconBox: styled.div`
		width: 46px;
		height: 46px;
		display: grid;
		place-items: center;
		border: 1px solid rgba(255, 153, 51, 0.42);
		background: rgba(255, 122, 0, 0.12);
		color: ${Colors.raw.neon_orange};
		box-shadow: 0 0 14px rgba(255, 122, 0, 0.12);

		svg {
			width: 26px;
			height: 26px;
		}
	`,

	NameBlock: styled.div`
		min-width: 0;
	`,

	StationName: styled.h2`
		margin: 0;
		color: ${Colors.raw.terminal_light};
		font-size: 20px;
		line-height: 1.15;
	`,

	TerminalName: styled.div`
		margin-top: 6px;
		color: ${Colors.raw.neon_orange};
		font-size: 12px;
		letter-spacing: 1.5px;
		text-transform: uppercase;
		line-height: 1.35;
	`,

	CapacityBadge: styled.div`
		flex-shrink: 0;
		border: 1px solid rgba(67, 255, 157, 0.45);
		background: rgba(67, 255, 157, 0.10);
		color: #8dffc5;
		padding: 8px 10px;
		font-size: 13px;
		font-weight: 800;
		font-family: "Courier New", monospace;
		white-space: nowrap;
	`,

	Route: styled.div`
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	`,

	RouteItem: styled.span`
		border: 1px solid rgba(255, 255, 255, 0.16);
		background: rgba(0, 0, 0, 0.24);
		color: ${Colors.raw.terminal_light};
		padding: 6px 8px;
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	RouteItemHighlight: styled.span`
		border: 1px solid rgba(255, 153, 51, 0.55);
		background: rgba(255, 122, 0, 0.14);
		color: ${Colors.raw.neon_orange};
		padding: 6px 8px;
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	Stats: styled.div`
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;

		@media (max-width: 520px) {
			grid-template-columns: 1fr;
		}
	`,

	Stat: styled.div`
		border: 1px solid rgba(255, 255, 255, 0.12);
		background: rgba(0, 0, 0, 0.22);
		padding: 10px;
		min-width: 0;
	`,

	StatLabel: styled.div`
		display: flex;
		align-items: center;
		gap: 6px;
		opacity: 0.58;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.4px;
		margin-bottom: 5px;

		svg {
			width: 13px;
			height: 13px;
		}
	`,

	StatValue: styled.div`
		color: ${Colors.raw.terminal_light};
		font-size: 14px;
		font-weight: 800;
		word-break: break-word;
	`,

	Footer: styled.div`
		display: flex;
		justify-content: space-between;
		gap: 12px;
		align-items: center;
		border-top: 1px solid rgba(255, 255, 255, 0.10);
		padding-top: 12px;
	`,

	ReportId: styled.div`
		opacity: 0.42;
		font-family: "Courier New", monospace;
		font-size: 11px;
	`,

	Updated: styled.div`
		opacity: 0.58;
		font-size: 11px;
		text-align: right;
	`,

	Loading: styled.div`
		padding: 28px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		opacity: 0.75;
		text-align: center;
	`,

	EmptyState: styled.div`
		padding: 30px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		background: rgba(255, 255, 255, 0.035);
		opacity: 0.78;
		text-align: center;
	`,
};

const formatNumber = (value?: number | null) =>
	value === null || value === undefined
		? "-"
		: new Intl.NumberFormat("de-DE").format(value);

const formatDate = (timestamp?: number) => {
	if (!timestamp) {
		return "-";
	}

	return new Date(timestamp * 1000).toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

const getLocationTitle = (location: IndustrialLocation) =>
	location.space_station_name ||
	location.outpost_name ||
	location.city_name ||
	location.poi_name ||
	location.terminal_name ||
	"Unbekannter Standort";

const getLocationPath = (location: IndustrialLocation) =>
	[
		location.star_system_name,
		location.planet_name,
		location.orbit_name,
		location.moon_name,
		location.city_name,
		location.outpost_name,
		location.poi_name,
	].filter(Boolean) as string[];

const LocationCard = ({location}: { location: IndustrialLocation }) => {
	const title = getLocationTitle(location);
	const path = getLocationPath(location);

	return (
		<Styles.Card>
			<Styles.CardTop>
				<Styles.IconBox>
					<SiArtstation  />
				</Styles.IconBox>

				<Styles.NameBlock>
					<Styles.StationName>{title}</Styles.StationName>
					<Styles.TerminalName>
						{location.terminal_name ?? "Kein Terminal angegeben"}
					</Styles.TerminalName>
				</Styles.NameBlock>

				<Styles.CapacityBadge>
					{formatNumber(location.value)}
				</Styles.CapacityBadge>
			</Styles.CardTop>

			<Styles.Route>
				{path.length === 0 ? (
					<Styles.RouteItem>Keine Positionsdaten</Styles.RouteItem>
				) : (
					path.map((part, index) =>
						index === 0 ? (
							<Styles.RouteItemHighlight key={`${part}-${index}`}>
								{part}
							</Styles.RouteItemHighlight>
						) : (
							<Styles.RouteItem key={`${part}-${index}`}>
								{part}
							</Styles.RouteItem>
						)
					)
				)}
			</Styles.Route>

			<Styles.Stats>
				<Styles.Stat>
					<Styles.StatLabel>
						<MdOutlineStorage />
						Aktuell
					</Styles.StatLabel>
					<Styles.StatValue>{formatNumber(location.value)}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>
						<GiMining />
						Woche
					</Styles.StatLabel>
					<Styles.StatValue>{formatNumber(location.value_week)}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>
						<IoPlanetOutline />
						Monat
					</Styles.StatLabel>
					<Styles.StatValue>{formatNumber(location.value_month)}</Styles.StatValue>
				</Styles.Stat>
			</Styles.Stats>

			<Styles.Footer>
				<Styles.ReportId>
					Report #{location.id_report} · Location #{location.id}
				</Styles.ReportId>

				<Styles.Updated>
					Update: {formatDate(location.date_modified)}
				</Styles.Updated>
			</Styles.Footer>
		</Styles.Card>
	);
};

export const IndustrialLocations = () => {

	const { locations } = useIndustrialTasks()

	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [systemFilter, setSystemFilter] = useState("all");
	const [planetFilter, setPlanetFilter] = useState("all");

	const systems = useMemo(
		() =>
			Array.from(
				new Set(
					locations
						.map(location => location.star_system_name)
						.filter((name): name is string => Boolean(name))
				)
			).sort((a, b) => a.localeCompare(b, "de")),
		[locations]
	);

	const planets = useMemo(
		() =>
			Array.from(
				new Set(
					locations
						.filter(location =>
							systemFilter === "all"
								? true
								: location.star_system_name === systemFilter
						)
						.map(location => location.planet_name)
						.filter((name): name is string => Boolean(name))
				)
			).sort((a, b) => a.localeCompare(b, "de")),
		[locations, systemFilter]
	);

	const visibleLocations = useMemo(() => {
		const normalizedSearchTerm = searchTerm.trim().toLowerCase();

		return [...locations]
			.filter(location =>
				systemFilter === "all"
					? true
					: location.star_system_name === systemFilter
			)
			.filter(location =>
				planetFilter === "all"
					? true
					: location.planet_name === planetFilter
			)
			.filter(location => {
				if (!normalizedSearchTerm) {
					return true;
				}

				const searchable = [
					location.star_system_name,
					location.planet_name,
					location.orbit_name,
					location.space_station_name,
					location.terminal_name,
					location.city_name,
					location.moon_name,
					location.outpost_name,
					location.poi_name,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();

				return searchable.includes(normalizedSearchTerm);
			})
			.sort((a, b) => b.value - a.value);
	}, [locations, planetFilter, searchTerm, systemFilter]);

	const totalCapacity = useMemo(
		() => visibleLocations.reduce((sum, location) => sum + location.value, 0),
		[visibleLocations]
	);

	const bestLocation = visibleLocations[0];

	return (
		<Styles.Page>
			<Styles.Header>
				<Styles.TitleBlock>
					<Styles.Eyebrow>Industrial Network</Styles.Eyebrow>
					<Styles.Title>Industrie-Anlagen</Styles.Title>
					<Styles.Subline>
						Übersicht aller Raffinerie- und Industrie-Terminals mit aktuellen
						Kapazitäten, Standortdaten und Wochen-/Monatswerten.
					</Styles.Subline>
				</Styles.TitleBlock>

				<Styles.Summary>
					<Styles.SummaryBox>
						<Styles.SummaryLabel>Standorte</Styles.SummaryLabel>
						<Styles.SummaryValue>
							{formatNumber(visibleLocations.length)}
						</Styles.SummaryValue>
					</Styles.SummaryBox>

					<Styles.SummaryBox>
						<Styles.SummaryLabel>Kapazität</Styles.SummaryLabel>
						<Styles.SummaryValue>
							{formatNumber(totalCapacity)}
						</Styles.SummaryValue>
					</Styles.SummaryBox>

					<Styles.SummaryBox>
						<Styles.SummaryLabel>Top Terminal</Styles.SummaryLabel>
						<Styles.SummaryValue>
							{bestLocation ? formatNumber(bestLocation.value) : "-"}
						</Styles.SummaryValue>
					</Styles.SummaryBox>
				</Styles.Summary>
			</Styles.Header>

			<Styles.Controls>
				<Styles.Input
					value={searchTerm}
					placeholder="Nach Station, Terminal, Orbit oder System suchen..."
					onChange={event => setSearchTerm(event.target.value)}
				/>

				<Styles.Select
					value={systemFilter}
					onChange={event => {
						setSystemFilter(event.target.value);
						setPlanetFilter("all");
					}}
				>
					<option value="all">Alle Systeme</option>
					{systems.map(system => (
						<option key={system} value={system}>
							{system}
						</option>
					))}
				</Styles.Select>

				<Styles.Select
					value={planetFilter}
					onChange={event => setPlanetFilter(event.target.value)}
				>
					<option value="all">Alle Planeten</option>
					{planets.map(planet => (
						<option key={planet} value={planet}>
							{planet}
						</option>
					))}
				</Styles.Select>
			</Styles.Controls>

			{isLoading && locations.length === 0 ? (
				<Styles.Loading>Industrie-Standorte werden geladen...</Styles.Loading>
			) : visibleLocations.length === 0 ? (
				<Styles.EmptyState>
					<MdLocationOn /> Keine Standorte für diese Auswahl gefunden.
				</Styles.EmptyState>
			) : (
				<Styles.Grid>
					{visibleLocations.map(location => (
						<LocationCard key={location.id} location={location} />
					))}
				</Styles.Grid>
			)}
		</Styles.Page>
	);
};